import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './IntakeFormsPage.module.css';

// A product that genuinely has no intake form yet legitimately opens an empty builder.
// Pure and module-scope so the shape is produced in exactly one place.
const createEmptyForm = (product) => ({
  name: `${product.name} Intake Form`,
  fields: [],
  settings: {
    showProgressBar: true,
    requiresDriversLicense: true
  }
});

// The admin catalogue does not fit in one page, and `/admin/products` sorts newest-first, so
// the products that actually own a questionnaire sort BEHIND the newest rows. A single unpaged
// fetch therefore lists a page on which no form exists. Page through the catalogue instead of
// raising the limit: a bigger constant is the same defect with a later trigger, and an
// unbounded fetch would grow without limit as the catalogue does.
const PRODUCT_PAGE_SIZE = 100;

const buildProductsQuery = (page) => `/admin/products?limit=${PRODUCT_PAGE_SIZE}&page=${page}`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'multi_select', label: 'Multi Select (Checkboxes)' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'height', label: 'Height (ft/in)' },
  { value: 'weight', label: 'Weight (lbs)' }
];

const IntakeFormsPage = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [commonQuestions, setCommonQuestions] = useState([]);
  const [globalQuestions, setGlobalQuestions] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showGlobalQuestions, setShowGlobalQuestions] = useState(true);
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load common questions
  useEffect(() => {
    const loadData = async () => {
      try {
        const [questionsRes, globalRes] = await Promise.all([
          api.get('/admin/intake-forms/common-questions'),
          api.get('/admin/intake-forms/global-questions')
        ]);
        setCommonQuestions(questionsRes.categories || []);
        setGlobalQuestions(globalRes.questions || []);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Load the catalogue one page at a time. Page 1 replaces the list and later pages append,
  // so every product stays reachable without ever issuing an unbounded fetch.
  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await api.get(buildProductsQuery(productPage));
        if (cancelled) return;
        const pageProducts = response.products || [];
        setProducts((previous) =>
          productPage === 1 ? pageProducts : [...previous, ...pageProducts]
        );
        setHasMoreProducts(Boolean(response.pagination?.hasNextPage));
      } catch (error) {
        if (!cancelled) toast.error('Failed to load products');
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    };
    loadProducts();
    return () => {
      cancelled = true;
    };
    // `toast` is a stable useMemo reference from ToastContext, so it cannot re-trigger this fetch.
  }, [productPage, toast]);

  // Load form when product selected
  const loadForm = useCallback(async () => {
    if (!selectedProduct) {
      setForm(null);
      setLoadError(null);
      return;
    }

    setLoadError(null);
    try {
      const productId = selectedProduct._id || selectedProduct.id;
      const response = await api.get(`/admin/intake-forms/product/${productId}`);
      // Ensure form has required properties with defaults
      const formData = response.form;
      if (formData) {
        setForm({
          ...formData,
          fields: formData.fields || [],
          settings: formData.settings || { showProgressBar: true, requiresDriversLicense: true }
        });
      } else {
        // The server answers the genuine "no form" case with 200 + { form: null }.
        setForm(createEmptyForm(selectedProduct));
      }
    } catch (error) {
      // ONLY a 404 means "this product has no form". Every other failure is a FAILURE:
      // synthesising an empty form here makes handleSaveForm take the CREATE branch, which
      // re-points the product at a new, nearly-empty form and orphans the real questionnaire.
      if (error?.status === 404) {
        setForm(createEmptyForm(selectedProduct));
      } else {
        setForm(null);
        setLoadError(error?.message || 'We could not load this intake form.');
      }
    }
  }, [selectedProduct]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const handleSaveForm = async () => {
    if (!selectedProduct || !form) return;

    setSaving(true);
    try {
      const formId = form._id || form.id;
      if (formId) {
        // Update existing form
        await api.patch(`/admin/intake-forms/${formId}`, form);
        toast.success('Intake form updated');
      } else {
        // Create new form and link to product
        const productId = selectedProduct._id || selectedProduct.id;
        const response = await api.post('/admin/intake-forms', {
          ...form,
          productId
        });
        setForm(response.form);
        toast.success('Intake form created');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    if (!form) return;
    const currentFields = Array.isArray(form.fields) ? form.fields : [];
    const newField = {
      fieldId: `field_${Date.now()}`,
      type: 'text',
      label: 'New Question',
      section: 'General',
      order: 0,
      validation: []
    };
    // Add new field at the beginning and update order values
    const updatedFields = [newField, ...currentFields].map((f, i) => ({ ...f, order: i }));
    setForm((prevForm) => ({ ...prevForm, fields: updatedFields }));
    setEditingField(newField.fieldId);
  };

  const updateField = (fieldId, updates) => {
    if (!form || !form.fields) return;
    setForm({
      ...form,
      fields: form.fields.map((f) =>
        f.fieldId === fieldId ? { ...f, ...updates } : f
      )
    });
  };

  const removeField = (fieldId) => {
    if (!form || !form.fields) return;
    setForm({
      ...form,
      fields: form.fields.filter((f) => f.fieldId !== fieldId)
    });
  };

  const moveField = (fieldId, direction) => {
    if (!form || !form.fields) return;
    const index = form.fields.findIndex((f) => f.fieldId === fieldId);
    if ((direction === -1 && index === 0) || (direction === 1 && index === form.fields.length - 1)) {
      return;
    }
    const newFields = [...form.fields];
    const [moved] = newFields.splice(index, 1);
    newFields.splice(index + direction, 0, moved);
    // Update order values
    const updatedFields = newFields.map((f, i) => ({ ...f, order: i }));
    setForm({ ...form, fields: updatedFields });
  };

  const moveFieldToPosition = (fieldId, newPosition) => {
    if (!form || !form.fields) return;
    const currentIndex = form.fields.findIndex((f) => f.fieldId === fieldId);
    if (currentIndex === -1) return;

    // Clamp position to valid range (1-based for user, 0-based internally)
    const targetIndex = Math.max(0, Math.min(form.fields.length - 1, newPosition - 1));
    if (targetIndex === currentIndex) return;

    const newFields = [...form.fields];
    const [moved] = newFields.splice(currentIndex, 1);
    newFields.splice(targetIndex, 0, moved);
    // Update order values
    const updatedFields = newFields.map((f, i) => ({ ...f, order: i }));
    setForm({ ...form, fields: updatedFields });
  };

  const toggleRequired = (fieldId) => {
    if (!form || !form.fields) return;
    const field = form.fields.find((f) => f.fieldId === fieldId);
    if (!field) return;
    const hasRequired = field.validation?.some((v) => v.type === 'required');

    updateField(fieldId, {
      validation: hasRequired
        ? field.validation.filter((v) => v.type !== 'required')
        : [...(field.validation || []), { type: 'required', message: 'This field is required' }]
    });
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Add common question to current form
  const addCommonQuestion = (question) => {
    if (!form || !selectedProduct) {
      toast.error('Please select a product first');
      return;
    }

    // Check if question already exists in form (by label)
    const exists = form.fields?.some(
      (f) => f.label.toLowerCase().trim() === question.label.toLowerCase().trim()
    );

    if (exists) {
      toast.error('This question is already in the form');
      return;
    }

    const currentFields = Array.isArray(form.fields) ? form.fields : [];
    const newField = {
      fieldId: `field_${Date.now()}`,
      type: question.type,
      label: question.label,
      section: question.section || 'General',
      order: 0,
      validation: []
    };

    // Add new field at the beginning and update order values
    const updatedFields = [newField, ...currentFields].map((f, i) => ({ ...f, order: i }));
    setForm((prevForm) => ({ ...prevForm, fields: updatedFields }));
    toast.success(`Added "${question.label}" to form`);
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.proTitle}>Intake <span className={styles.proText}>PRO</span></h1>
        <p>Manage intake form questions for each product</p>
      </div>

      <div className={styles.layout}>
        {/* Common Questions + Product List */}
        <div className={styles.sidebarWrapper}>
          {/* Common Questions Section */}
          {(globalQuestions.length > 0 || commonQuestions.length > 0) && (
            <div className={styles.commonQuestionsSection}>
              <h3 className={styles.commonQuestionsTitle}>
                <span className={styles.proIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                Common Questions
              </h3>

              <div className={styles.categoriesList}>
                {/* Global Common Questions */}
                {globalQuestions.length > 0 && (
                  <div className={styles.categoryAccordion}>
                    <button
                      className={`${styles.categoryHeader} ${styles.globalHeader} ${showGlobalQuestions ? styles.expanded : ''}`}
                      onClick={() => setShowGlobalQuestions(!showGlobalQuestions)}
                    >
                      <span className={styles.categoryName}>
                        <span className={styles.globalBadge}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        </span>
                        Global (All Categories)
                      </span>
                      <span className={styles.categoryCount}>{globalQuestions.length}</span>
                      <span className={styles.expandIcon}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points={showGlobalQuestions ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                        </svg>
                      </span>
                    </button>
                    {showGlobalQuestions && (
                      <div className={styles.questionsList}>
                        {globalQuestions.map((question, idx) => (
                          <button
                            key={idx}
                            className={`${styles.questionItem} ${styles.clickable}`}
                            onClick={() => addCommonQuestion(question)}
                            title={selectedProduct ? `Add "${question.label}" to ${selectedProduct.name}` : 'Select a product first'}
                          >
                            <div className={styles.questionLabel}>
                              {question.label}
                              <span className={styles.addIndicator}>+</span>
                            </div>
                            <div className={styles.questionMeta}>
                              <span className={styles.questionType}>
                                {FIELD_TYPES.find((t) => t.value === question.type)?.label || question.type}
                              </span>
                              <span className={styles.usageCount}>
                                Used {question.usageCount}x
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Per-Category Common Questions */}
                {commonQuestions.map((category) => (
                  <div key={category.categoryId} className={styles.categoryAccordion}>
                    <button
                      className={`${styles.categoryHeader} ${expandedCategories[category.categoryId] ? styles.expanded : ''}`}
                      onClick={() => toggleCategory(category.categoryId)}
                    >
                      <span className={styles.categoryName}>{category.categoryName}</span>
                      <span className={styles.categoryCount}>{category.questions.length}</span>
                      <span className={styles.expandIcon}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points={expandedCategories[category.categoryId] ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                        </svg>
                      </span>
                    </button>
                    {expandedCategories[category.categoryId] && (
                      <div className={styles.questionsList}>
                        {category.questions.map((question, idx) => (
                          <button
                            key={idx}
                            className={`${styles.questionItem} ${styles.clickable}`}
                            onClick={() => addCommonQuestion(question)}
                            title={selectedProduct ? `Add "${question.label}" to ${selectedProduct.name}` : 'Select a product first'}
                          >
                            <div className={styles.questionLabel}>
                              {question.label}
                              <span className={styles.addIndicator}>+</span>
                            </div>
                            <div className={styles.questionMeta}>
                              <span className={styles.questionType}>
                                {FIELD_TYPES.find((t) => t.value === question.type)?.label || question.type}
                              </span>
                              <span className={styles.usageCount}>
                                Used {question.usageCount}x
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product List */}
          <div className={styles.sidebar}>
            <h3>Products</h3>
            <div className={styles.productList}>
              {products.map((product) => (
                <button
                  key={product._id || product.id}
                  className={`${styles.productItem} ${(selectedProduct?._id || selectedProduct?.id) === (product._id || product.id) ? styles.active : ''}`}
                  onClick={() => setSelectedProduct(product)}
                >
                  <span className={styles.productName}>{product.name}</span>
                  <span className={styles.productCategory}>{product.category?.name || 'Uncategorized'}</span>
                </button>
              ))}
            </div>
            {hasMoreProducts && (
              <button
                type="button"
                className={styles.addBtn}
                onClick={() => setProductPage((page) => page + 1)}
                disabled={loadingProducts}
              >
                {loadingProducts ? 'Loading...' : 'Load more products'}
              </button>
            )}
          </div>
        </div>

        {/* Form Editor */}
        <div className={styles.editor}>
          {!selectedProduct ? (
            <div className={styles.placeholder}>
              <p>Select a product to edit its intake form</p>
            </div>
          ) : loadError ? (
            <div className={styles.placeholder}>
              <p>We couldn&apos;t load this product&apos;s intake form.</p>
              <button className={styles.addBtn} onClick={loadForm}>
                Try again
              </button>
            </div>
          ) : !form ? (
            <div className={styles.loading}>Loading form...</div>
          ) : (
            <>
              <div className={styles.editorHeader}>
                <div>
                  <h2>{selectedProduct.name}</h2>
                  <span className={styles.formName}>{form.name}</span>
                </div>
                <div className={styles.editorActions}>
                  <button className={styles.addBtn} onClick={addField}>
                    + Add Question
                  </button>
                  <button
                    className={styles.saveBtn}
                    onClick={handleSaveForm}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Form'}
                  </button>
                </div>
              </div>

              {/* Settings */}
              <div className={styles.settings}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={form.settings?.requiresDriversLicense !== false}
                    onChange={(e) => setForm({
                      ...form,
                      settings: { ...form.settings, requiresDriversLicense: e.target.checked }
                    })}
                  />
                  <span>Require Driver's License Upload</span>
                </label>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={form.settings?.showProgressBar !== false}
                    onChange={(e) => setForm({
                      ...form,
                      settings: { ...form.settings, showProgressBar: e.target.checked }
                    })}
                  />
                  <span>Show Progress Bar</span>
                </label>
              </div>

              {/* Fields */}
              <div className={styles.fields}>
                {(!form.fields || form.fields.length === 0) ? (
                  <div className={styles.emptyFields}>
                    <p>No questions added yet</p>
                    <button className={styles.addBtn} onClick={addField}>
                      + Add First Question
                    </button>
                  </div>
                ) : (
                  form.fields.map((field, index) => (
                    <div key={field.fieldId} className={styles.fieldCard}>
                      <div className={styles.fieldHeader}>
                        <div className={styles.fieldOrder}>
                          <button
                            onClick={() => moveField(field.fieldId, -1)}
                            disabled={index === 0}
                          >
                            ↑
                          </button>
                          <span>{index + 1}</span>
                          <button
                            onClick={() => moveField(field.fieldId, 1)}
                            disabled={index === form.fields.length - 1}
                          >
                            ↓
                          </button>
                        </div>
                        <div className={styles.fieldActions}>
                          <button
                            className={styles.editBtn}
                            onClick={() => setEditingField(
                              editingField === field.fieldId ? null : field.fieldId
                            )}
                          >
                            {editingField === field.fieldId ? 'Done' : 'Edit'}
                          </button>
                          <button
                            className={styles.deleteBtn}
                            onClick={() => removeField(field.fieldId)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {editingField === field.fieldId ? (
                        <div className={styles.fieldEdit}>
                          <div className={styles.fieldRow}>
                            <label>Question</label>
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateField(field.fieldId, { label: e.target.value })}
                              placeholder="Enter question text"
                            />
                          </div>

                          <div className={styles.fieldRow}>
                            <label>Position</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <select
                                value={index + 1}
                                onChange={(e) => moveFieldToPosition(field.fieldId, parseInt(e.target.value))}
                                style={{ width: '100px', padding: '0.625rem 0.875rem', borderRadius: '8px', border: '0.5px solid #f3e5d0' }}
                              >
                                {form.fields.map((_, i) => (
                                  <option key={i} value={i + 1}>{i + 1}</option>
                                ))}
                              </select>
                              <span style={{ fontSize: '0.75rem', color: '#737373' }}>of {form.fields.length}</span>
                            </div>
                          </div>

                          <div className={styles.fieldRow}>
                            <label>Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => updateField(field.fieldId, { type: e.target.value })}
                            >
                              {FIELD_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className={styles.fieldRow}>
                            <label>Section</label>
                            <input
                              type="text"
                              value={field.section || ''}
                              onChange={(e) => updateField(field.fieldId, { section: e.target.value })}
                              placeholder="e.g., Personal Information"
                            />
                          </div>

                          {['select', 'radio', 'multi_select', 'checkbox', 'multiselect'].includes(field.type) && (
                            <div className={styles.fieldRow}>
                              <label>Options (one per line)</label>
                              <textarea
                                value={(field.options || []).map((o) => typeof o === 'string' ? o : o.label).join('\n')}
                                onChange={(e) => updateField(field.fieldId, {
                                  options: e.target.value.split('\n').filter(Boolean).map((label) => ({
                                    value: label.toLowerCase().replace(/\s+/g, '_'),
                                    label
                                  }))
                                })}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                                rows={4}
                              />
                            </div>
                          )}

                          {['select', 'radio', 'multi_select', 'checkbox', 'multiselect'].includes(field.type) && (
                            <div className={styles.fieldRow}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={field.allow_other || false}
                                  onChange={(e) => updateField(field.fieldId, { allow_other: e.target.checked })}
                                  style={{ width: '18px', height: '18px' }}
                                />
                                Allow "Other" option
                              </label>
                              <span style={{ fontSize: '0.75rem', color: '#737373' }}>
                                Adds an "Other (please specify)" option with a text input
                              </span>
                            </div>
                          )}

                          <div className={styles.fieldRow}>
                            <label>Placeholder</label>
                            <input
                              type="text"
                              value={field.placeholder || ''}
                              onChange={(e) => updateField(field.fieldId, { placeholder: e.target.value })}
                              placeholder="Optional placeholder text"
                            />
                          </div>

                          <div className={styles.fieldRow}>
                            <label className={styles.checkbox}>
                              <input
                                type="checkbox"
                                checked={field.validation?.some((v) => v.type === 'required')}
                                onChange={() => toggleRequired(field.fieldId)}
                              />
                              <span>Required</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fieldPreview}>
                          <div className={styles.fieldLabel}>
                            {field.label}
                            {field.validation?.some((v) => v.type === 'required') && (
                              <span className={styles.required}>*</span>
                            )}
                          </div>
                          <div className={styles.fieldMeta}>
                            <span className={styles.fieldType}>
                              {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                            </span>
                            {field.section && (
                              <span className={styles.fieldSection}>{field.section}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeFormsPage;
